import _ from 'lodash';
import { observable, action, computed, observe } from 'mobx';

class AuthStore {
    rootStore;

    @observable accessor token = undefined;
    @observable accessor signInRedirect = null;
    rootStore = null;

    constructor(rootStore) {

        this.rootStore = rootStore;

        // sync local token with baasic app token
        observe(this, 'isAuthenticated',
            async (
                // eslint-disable-next-line
                { oldValue, newValue }
                ) => {
                if (!newValue) {
                    //TODO change to resolveUser - remove user
                    const { userStore } = this.rootStore;
                    userStore.removeUser();

                    this.rootStore.permissionStore.resetPermissions();

                    const currentRoute = this.rootStore.routerStore.routerState;
                    if (!currentRoute.isPublic) {
                        this.rootStore.navigateLogin();
                    }
                }
            }
        );

        // Event wiring deferred to initialize() — apiKey isn't available until applicationStore exists.
    }

    @computed
    get isAuthenticated() {
        return isTokenValid(this.token);
    }

    getSignInRedirect() {
        if (this.signInRedirect) {
            return this.signInRedirect;
        }

        return this.rootStore.initialState;
    }

    @action
    syncToken() {
        const { application } = this.rootStore;
        this.token = application && application.baasic
            ? application.baasic.getAccessToken() || null
            : null
    }

    // The SDK keeps a per-tab expiration timer that is only restarted by requests made in that tab.
    // When another tab refreshes the shared sliding token, restart this tab's timer too, otherwise
    // an idle tab's timer expires and clears the token for every open tab.
    syncExpirationTimer() {
        const { application } = this.rootStore;
        if (!application || !application.baasic) {
            return;
        }
        const token = application.baasic.getAccessToken();
        if (token) {
            application.baasic.tokenHandler.syncToken(token);
        }
    }

    @action
    setSignInRedirect(state) {
        this.signInRedirect = state;
    }

    resetSignInRedirect() {
        this.setSignInRedirect(null);
    }

    async initialize() {
        this.initAuthEvents();
        this.syncToken();
        return Promise.resolve();
    }

    initAuthEvents() {
        // baasic-sdk-javascript 2.0.12 renamed the dispatched events from "tokenUpdated" to "tokenUpdated-<apiKey>"; subscribe to both for compatibility.
        const { application } = this.rootStore;
        const apiKey = application && application.baasic && application.baasic.getApiKey
            ? application.baasic.getApiKey()
            : null;
        const updatedNames = apiKey ? ['tokenUpdated', `tokenUpdated-${apiKey}`] : ['tokenUpdated'];
        const expiredNames = apiKey ? ['tokenExpired', `tokenExpired-${apiKey}`] : ['tokenExpired'];

        // we need to check for jQuery and add event listeners since the bootstrap triggers those instead
        // of the native ones when it finds jQuery in the global namespace
        if (window.jQuery) {
            const $ = window.jQuery;
            expiredNames.forEach(name => $(document).on(name, async () => { this.syncToken(); }));
            updatedNames.forEach(name => $(document).on(name, async () => { this.syncToken(); this.syncExpirationTimer(); }));
            return;
        }

        expiredNames.forEach(name => document.addEventListener(name, () => { this.syncToken(); }));
        updatedNames.forEach(name => document.addEventListener(name, async () => { this.syncToken(); this.syncExpirationTimer(); }));
    }
}

function isTokenValid(token) {
    return !_.isNil(token) &&
        (token.expireTime === undefined
            || token.expireTime === null
            || (token.expireTime - new Date().getTime()) > 0
    );
}

export default AuthStore;
